import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@wehunt/core-web'
import type { PinVisibilityControls } from '@wehunt/ui'
import {
  getMyPinVisibility,
  updateMyPinVisibility,
  type MyPinVisibilityDto,
  type PinVisibilityChange,
} from './api/myPinVisibilityApi'

/**
 * How long a burst of switches is gathered before it is saved. A folder's
 * switch arrives as one call per pin and line in it, and those belong in one
 * request rather than one each.
 */
export const VISIBILITY_SAVE_DELAY_MS = 400

/** Pending switches: id → hidden. The latest flip of an id wins. */
type Pending = { pins: Map<string, boolean>; collections: Map<string, boolean> }

const emptyPending = (): Pending => ({ pins: new Map(), collections: new Map() })

function overlay(base: readonly string[], ...layers: Map<string, boolean>[]): Set<string> {
  const out = new Set(base)
  for (const layer of layers) {
    for (const [id, hidden] of layer) {
      if (hidden) out.add(id)
      else out.delete(id)
    }
  }
  return out
}

function toChange(p: Pending): PinVisibilityChange {
  const split = (m: Map<string, boolean>, hidden: boolean) =>
    [...m].filter(([, h]) => h === hidden).map(([id]) => id)
  return {
    hidePinIds: split(p.pins, true),
    showPinIds: split(p.pins, false),
    hideCollectionIds: split(p.collections, true),
    showCollectionIds: split(p.collections, false),
  }
}

/**
 * Which pins and lines the viewer is choosing not to draw on a team's map.
 *
 * Per viewer, never per team: hiding a pin to read the ground underneath it
 * changes what this hunter sees and nothing anyone else does — which is why
 * every member gets the switches, not just the team's admins. The choice is
 * kept where the app keeps it, on the hunter's own user document
 * (`hiddenPinIds`, `hiddenCollectionIds`), so one hunter switching a drive's
 * folder off in the browser finds it off on the phone at the next drive, and
 * the other way round. It used to live in this page's memory and was gone on
 * the next visit.
 *
 * The collection master is the team's id in `hiddenCollectionIds`, as in the
 * app, rather than "add every id to the hidden set", so turning it back on
 * restores exactly what was showing before. It also switches the team's LINES
 * off one by one, because that is what the app does: its live map filters pins
 * by the collection id but not lines, so a master switch that left the lines
 * alone would hide the team's pins on the phone and keep drawing its lines.
 * Turning it back on restores the lines that were showing before it went off.
 *
 * Switches show at once and are saved shortly after, gathered into one change.
 * A save that fails puts the switches back where the server has them and calls
 * `onSaveFailed`, rather than leaving the panel claiming a state nothing holds.
 */
export function useMapPinVisibility(
  teamId: string,
  teamLineIds: readonly string[],
  onSaveFailed?: () => void,
): PinVisibilityControls {
  const { state } = useAuth()
  const isSignedIn = state.status === 'signed-in'
  const uid = isSignedIn ? state.user.uid : null
  const queryClient = useQueryClient()
  const queryKey = useMemo(() => ['clubhouse', 'me', uid, 'map-pin-visibility'], [uid])

  const getToken = useCallback(
    () => (state as Extract<typeof state, { status: 'signed-in' }>).user.getIdToken(),
    [state],
  )

  const { data } = useQuery({
    queryKey,
    enabled: isSignedIn,
    // The phone writes these lists too, so a copy from an earlier visit is no
    // answer for what this one should show.
    staleTime: 0,
    queryFn: async () => getMyPinVisibility(await getToken()),
  })

  // Switches not yet saved, and switches being saved. Both sit over the
  // server's lists until the save that carries them answers.
  const pending = useRef<Pending>(emptyPending())
  const inFlight = useRef<Pending>(emptyPending())
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [, setRevision] = useState(0)
  const rerender = useCallback(() => setRevision((r) => r + 1), [])

  const onSaveFailedRef = useRef(onSaveFailed)
  onSaveFailedRef.current = onSaveFailed

  const flush = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
    const batch = pending.current
    if (batch.pins.size === 0 && batch.collections.size === 0) return
    pending.current = emptyPending()
    for (const [id, h] of batch.pins) inFlight.current.pins.set(id, h)
    for (const [id, h] of batch.collections) inFlight.current.collections.set(id, h)

    const settle = () => {
      for (const [id, h] of batch.pins) {
        if (inFlight.current.pins.get(id) === h) inFlight.current.pins.delete(id)
      }
      for (const [id, h] of batch.collections) {
        if (inFlight.current.collections.get(id) === h) inFlight.current.collections.delete(id)
      }
    }

    void (async () => {
      try {
        const written = await updateMyPinVisibility(toChange(batch), await getToken())
        queryClient.setQueryData<MyPinVisibilityDto>(queryKey, written)
        settle()
      } catch {
        settle()
        void queryClient.invalidateQueries({ queryKey })
        onSaveFailedRef.current?.()
      }
      rerender()
    })()
  }, [getToken, queryClient, queryKey, rerender])

  const schedule = useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(flush, VISIBILITY_SAVE_DELAY_MS)
    rerender()
  }, [flush, rerender])

  // Leaving the page must not drop switches still waiting for the delay.
  useEffect(() => flush, [flush])

  const hiddenPinIds = overlay(data?.hiddenPinIds ?? [], inFlight.current.pins, pending.current.pins)
  const hiddenCollectionIds = overlay(
    data?.hiddenCollectionIds ?? [],
    inFlight.current.collections,
    pending.current.collections,
  )
  const collectionHidden = hiddenCollectionIds.has(teamId)

  // The team lines that were showing when the master went off, to bring back
  // when it comes on. Only this page's own memory, as in the app: once it is
  // gone, the master comes back on with the lines as they are.
  const linesBeforeHide = useRef<string[] | null>(null)

  const onSetPinHidden = useCallback(
    (id: string, hidden: boolean) => {
      pending.current.pins.set(id, hidden)
      schedule()
    },
    [schedule],
  )

  const onSetCollectionHidden = useCallback(
    (hidden: boolean) => {
      pending.current.collections.set(teamId, hidden)
      if (hidden) {
        linesBeforeHide.current = teamLineIds.filter((id) => !hiddenPinIds.has(id))
        for (const id of teamLineIds) pending.current.pins.set(id, true)
      } else {
        const restore = linesBeforeHide.current ?? teamLineIds.filter((id) => !hiddenPinIds.has(id))
        for (const id of restore) pending.current.pins.set(id, false)
        linesBeforeHide.current = null
      }
      schedule()
    },
    // hiddenPinIds is rebuilt every render; reading the latest is the point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [teamId, teamLineIds, schedule, hiddenPinIds],
  )

  return { hiddenPinIds, onSetPinHidden, collectionHidden, onSetCollectionHidden }
}
